"use client";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { logger } from "@/lib/logger";
import { SPECIAL_VIEW_IDS } from "../../sortable/sortable-types";
import {
  createSidebarSearchTree,
  filterSidebarSearchTree,
  type SidebarSearchProjectMatch,
  type SidebarSearchResult,
  type SidebarSearchTree,
} from "./sidebar-search";
import type {
  SidebarSearchGroupSource,
  SidebarSearchSources,
} from "./sidebar-search-contract";
import type { ProjectGroupType } from "./types";

const SIDEBAR_SEARCH_GROUP_LABEL_MESSAGES = {
  dashboards: msg`DASHBOARDS`,
  iw: msg`INDEX WORKER`,
  sa: msg`SEARCH ANALYTICS`,
  ua: msg`USER ANALYTICS`,
  ads: msg`ADS ANALYTICS`,
} satisfies Record<ProjectGroupType, MessageDescriptor>;

const SIDEBAR_SEARCH_ITEM_LABEL_MESSAGES: Partial<
  Record<string, MessageDescriptor>
> = {
  [SPECIAL_VIEW_IDS.ALL]: msg({ id: "default-view.name", message: "All" }),
  [SPECIAL_VIEW_IDS.SESSION_EXPLORER]: msg`Session Explorer`,
  [SPECIAL_VIEW_IDS.INITIAL_ANALYSIS]: msg`Continue initial analysis`,
};

type SidebarSearchContextValue = {
  query: string;
  setQuery: (query: string) => void;
  result: SidebarSearchResult;
};

const SidebarSearchContext = createContext<SidebarSearchContextValue | null>(
  null,
);

export function SidebarSearchProvider({
  tree,
  children,
}: {
  tree: SidebarSearchTree;
  children: ReactNode;
}) {
  const [query, setQuery] = useState("");
  const result = useMemo(
    () => filterSidebarSearchTree(tree, query),
    [query, tree],
  );
  const value = useMemo(() => ({ query, setQuery, result }), [query, result]);

  return (
    <SidebarSearchContext.Provider value={value}>
      {children}
    </SidebarSearchContext.Provider>
  );
}

/**
 * 表示中の Lingui locale で固定ラベルを補完し、検索 tree と状態を提供します。
 * Server Component から locale を中継せず、翻訳と検索条件を同じ Client 境界に保ちます。
 */
export function LocalizedSidebarSearchProvider({
  sources,
  workspaceId,
  children,
}: {
  sources: SidebarSearchSources;
  workspaceId: string;
  children: ReactNode;
}) {
  const lingui = useLingui();
  const { tree, diagnostics } = useMemo(() => {
    const sourceWithLocalizedLabels = (
      groupType: ProjectGroupType,
    ): SidebarSearchGroupSource => {
      const source = sources[groupType];
      if (!source.isVisible) {
        return source;
      }

      return {
        ...source,
        label: lingui.i18n._(SIDEBAR_SEARCH_GROUP_LABEL_MESSAGES[groupType]),
        projects: source.projects.map((project) => ({
          ...project,
          items: project.items.map((item) => {
            const message = SIDEBAR_SEARCH_ITEM_LABEL_MESSAGES[item.id];
            return {
              ...item,
              label: item.label ?? (message ? lingui.i18n._(message) : null),
            };
          }),
        })),
      };
    };
    const localizedSources: SidebarSearchSources = {
      dashboards: sourceWithLocalizedLabels("dashboards"),
      iw: sourceWithLocalizedLabels("iw"),
      sa: sourceWithLocalizedLabels("sa"),
      ua: sourceWithLocalizedLabels("ua"),
      ads: sourceWithLocalizedLabels("ads"),
    };

    return createSidebarSearchTree(localizedSources);
  }, [lingui, sources]);

  useEffect(() => {
    const diagnosticCounts = new Map<string, number>();
    for (const diagnostic of diagnostics) {
      const key = `${diagnostic.groupType}:${diagnostic.reason}`;
      diagnosticCounts.set(key, (diagnosticCounts.get(key) ?? 0) + 1);
    }
    for (const [key, count] of diagnosticCounts) {
      const [group, reason] = key.split(":");
      logger.warn("Sidebar search index entry was skipped", {
        workspaceId,
        group,
        phase: "index",
        reason,
        count,
      });
    }
  }, [diagnostics, workspaceId]);

  return <SidebarSearchProvider tree={tree}>{children}</SidebarSearchProvider>;
}

export function useSidebarSearch() {
  const context = useContext(SidebarSearchContext);
  return {
    query: context?.query ?? "",
    setQuery: context?.setQuery ?? (() => {}),
    isFiltering: Boolean(context?.result.query),
    hasMatches: (context?.result.groups.length ?? 1) > 0,
  };
}

export function useSidebarSearchGroupMatch(groupType: ProjectGroupType) {
  const context = useContext(SidebarSearchContext);
  const isFiltering = Boolean(context?.result.query);
  const groupMatch = context?.result.groups.find(
    (group) => group.id === groupType,
  );

  return {
    isFiltering,
    isVisible: !isFiltering || Boolean(groupMatch),
  };
}

export function useSidebarSearchGroupProjects(groupType: ProjectGroupType) {
  const context = useContext(SidebarSearchContext);
  const isFiltering = Boolean(context?.result.query);
  const projects =
    context?.result.groups.find((group) => group.id === groupType)?.projects ??
    [];

  return { isFiltering, projects };
}

export function useSidebarSearchProjectMatch(
  groupType: ProjectGroupType,
  projectId: string,
): {
  isFiltering: boolean;
  isVisible: boolean;
  match: SidebarSearchProjectMatch | null;
} {
  const context = useContext(SidebarSearchContext);
  const isFiltering = Boolean(context?.result.query);
  const match =
    context?.result.groups
      .find((group) => group.id === groupType)
      ?.projects.find((project) => project.id === projectId) ?? null;

  return {
    isFiltering,
    isVisible: !isFiltering || Boolean(match),
    match,
  };
}

/**
 * 検索結果に含まれる project 配下の固定行だけを表示するための可視性を返します。
 * 通常表示では常に表示し、検索中は project 全体または対象 item が一致した場合に限定します。
 */
export function useSidebarSearchItemVisibility(
  groupType: ProjectGroupType,
  projectId: string,
  itemId: string,
): boolean {
  const { isFiltering, match } = useSidebarSearchProjectMatch(
    groupType,
    projectId,
  );
  return (
    !isFiltering ||
    Boolean(match?.includeAllItems || match?.itemIds.includes(itemId))
  );
}

/** 検索 index に含めない補助導線を、通常表示のときだけ描画します。 */
export function SidebarSearchUnfilteredOnly({
  children,
}: {
  children: ReactNode;
}) {
  const context = useContext(SidebarSearchContext);
  return context?.result.query ? null : children;
}
